import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, RefreshCw, ExternalLink, Search, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CanvaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.13 16.78c-2.69 0-4.36-1.94-4.36-4.78 0-2.84 1.7-4.78 4.4-4.78 1.96 0 3.21.99 3.66 2.36.07.2-.04.32-.23.32h-1.41c-.14 0-.22-.07-.28-.21-.27-.55-.79-.94-1.74-.94-1.27 0-2.07.99-2.07 2.55v1.4c0 1.56.81 2.55 2.07 2.55.96 0 1.5-.4 1.78-.96.07-.13.14-.21.28-.21h1.41c.19 0 .3.13.23.32-.46 1.41-1.74 2.38-3.74 2.38z"/>
  </svg>
);

const ImportModal = ({ design, onClose, onImported }) => {
  const [name, setName] = useState(design?.title || `Canva ${design?.id?.slice(0, 6)}`);
  const [type, setType] = useState('social_post');
  const [showcase, setShowcase] = useState(false);
  const [parseLayers, setParseLayers] = useState(true);
  const [importing, setImporting] = useState(false);

  if (!design) return null;

  const submit = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/canva/designs/${design.id}/import`,
        { template_name: name, template_type: type, is_showcase: showcase, parse_layers: parseLayers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const parsedCount = data.layers_parsed || 0;
      const msg = parseLayers && parsedCount > 1
        ? `Imported "${data.name}" with ${parsedCount} editable layers`
        : `Imported "${data.name}"`;
      toast.success(msg);
      onImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="canva-import-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Import from Canva</h3>
            <p className="text-xs text-gray-500 mt-0.5">{design.title || 'Untitled design'}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="import-close"><X className="h-4 w-4" /></button>
        </div>

        {design.thumbnail?.url && (
          <img src={design.thumbnail.url} alt={design.title || 'preview'} className="w-full h-40 object-contain bg-gray-50 rounded-lg" />
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Template name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="import-name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Template type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm" data-testid="import-type-select">
              <option value="social_post">Social Post</option>
              <option value="story">Story / Reel cover</option>
              <option value="flyer">Flyer</option>
              <option value="postcard">Postcard</option>
              <option value="reel">Reel</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={parseLayers} onChange={(e) => setParseLayers(e.target.checked)} data-testid="import-parse-layers-toggle" />
            <span>Auto-parse into editable layers <span className="text-gray-400">(AI Vision)</span></span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={showcase} onChange={(e) => setShowcase(e.target.checked)} data-testid="import-showcase-toggle" />
            <span>Show on public landing page</span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} disabled={importing}>Cancel</Button>
          <Button onClick={submit} disabled={importing || !name} className="bg-black hover:bg-black/90" data-testid="import-confirm-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Import as template
          </Button>
        </div>
      </div>
    </div>
  );
};

export const CanvaPanel = ({ onImported }) => {
  const [status, setStatus] = useState({ connected: null });
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [continuation, setContinuation] = useState(null);
  const [selected, setSelected] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/canva/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false });
    }
  }, []);

  const fetchDesigns = useCallback(async (query = '', cont = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (query) params.q = query;
      if (cont) params.continuation = cont;
      const { data } = await axios.get(`${API}/canva/designs`, { params, headers: { Authorization: `Bearer ${token}` } });
      setDesigns(prev => cont ? [...prev, ...(data.items || [])] : (data.items || []));
      setContinuation(data.continuation || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load designs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // If we just came back from OAuth, show toast + reload designs
    const params = new URLSearchParams(window.location.search);
    const canvaParam = params.get('canva');
    if (canvaParam === 'connected') {
      toast.success('Canva connected');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (canvaParam === 'denied') {
      toast.error('You denied the Canva permissions request');
    } else if (canvaParam === 'failed' || canvaParam === 'invalid' || canvaParam === 'expired') {
      toast.error('Canva connection failed — please try again');
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (status.connected) fetchDesigns('');
  }, [status.connected, fetchDesigns]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/canva/connect`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Canva connection');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Canva account?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/canva/disconnect`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Canva disconnected');
      setStatus({ connected: false });
      setDesigns([]);
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDesigns(q);
  };

  if (status.connected === null) {
    return (
      <div className="rounded-2xl border border-gray-200 p-8 flex items-center justify-center text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking Canva connection…
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-cyan-50 to-violet-50 p-8 text-center" data-testid="canva-disconnected" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-white shadow-sm mb-4 text-cyan-600">
          <CanvaIcon />
        </div>
        <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Bring your Canva designs into RealProp</h3>
        <p className="text-sm text-gray-600 mt-1 mb-5 max-w-md mx-auto">Connect your Canva account once. Then import any design as a RealProp template — listings, social posts, flyers, the works.</p>
        <Button onClick={handleConnect} disabled={connecting} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="canva-connect-btn">
          {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
          Connect Canva
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="canva-connected">
      <div className="flex items-center justify-between gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your Canva designs…"
            className="border-gray-200"
            data-testid="canva-search-input"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          />
          <Button type="submit" variant="outline" size="sm" data-testid="canva-search-btn">Search</Button>
        </form>
        <Button variant="ghost" size="sm" onClick={() => fetchDesigns(q)} disabled={loading} data-testid="canva-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleDisconnect} className="text-gray-500" data-testid="canva-disconnect-btn">
          Disconnect
        </Button>
      </div>

      {loading && designs.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 p-12 flex items-center justify-center text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading designs…
        </div>
      ) : designs.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 p-12 text-center text-gray-500 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
          No designs found. Try a different search.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {designs.map(d => (
              <div key={d.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow group" data-testid={`canva-design-${d.id}`}>
                <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                  {d.thumbnail?.url ? (
                    <img src={d.thumbnail.url} alt={d.title || 'Canva design'} className="w-full h-full object-contain" />
                  ) : (
                    <CanvaIcon />
                  )}
                </div>
                <div className="p-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="text-sm font-medium truncate text-black">{d.title || 'Untitled'}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">{d.page_count || 1} page{(d.page_count || 1) > 1 ? 's' : ''}</div>
                  <Button size="sm" onClick={() => setSelected(d)} className="w-full mt-2 bg-black hover:bg-black/90 text-white text-xs" data-testid={`import-design-${d.id}`}>
                    <Plus className="h-3 w-3 mr-1" /> Import
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {continuation && (
            <div className="text-center pt-2">
              <Button variant="outline" onClick={() => fetchDesigns(q, continuation)} disabled={loading} data-testid="canva-load-more">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      {selected && (
        <ImportModal
          design={selected}
          onClose={() => setSelected(null)}
          onImported={(result) => {
            setSelected(null);
            if (onImported) onImported(result);
          }}
        />
      )}
    </div>
  );
};

export default CanvaPanel;
